import { format } from 'date-fns';
import { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { ScrollView as GestureHandlerScrollView } from 'react-native-gesture-handler';

import { IconCheck, InlineAlert, Text, ScrollView as UIScrollView } from '@fhs/ui';
import { PICKUP_TIME_SELECTOR_TIME_PICKER } from '@fhs-legacy/frontend/src/utils/test-ids';

import { getEarliestAvailablePickupTime } from './earliest-pickup-time';

// GestureHandlerScrollView on native to work properly inside ActionSheet on Android
const ScrollView = (Platform.OS === 'web' ? UIScrollView : GestureHandlerScrollView) as any;

export default function TimePicker({
  datePickerData,
  selectedDay,
  selectedSlot,
  setSelectedSlot,
  earliestPickupTimeInSeconds = 0,
  todayStoreOpensAt,
  isRestaurantOpen: _isRestaurantOpen,
  warningMessage = '',
}) {
  const earliestAvailablePickupTime = useMemo(
    () =>
      getEarliestAvailablePickupTime(earliestPickupTimeInSeconds, {
        storeOpensAt: todayStoreOpensAt,
      }),
    [earliestPickupTimeInSeconds, todayStoreOpensAt]
  );

  const times = useMemo(() => {
    const dayOptions = datePickerData[selectedDay]?.options ?? [];
    return dayOptions.filter(
      option => option.time.getTime() >= earliestAvailablePickupTime.getTime()
    );
  }, [datePickerData, selectedDay, earliestAvailablePickupTime]);

  return (
    <View testID={PICKUP_TIME_SELECTOR_TIME_PICKER} style={styles.container}>
      <Text.Heading type="three" style={styles.heading}>
        Select Time
      </Text.Heading>

      {warningMessage ? (
        <View style={styles.warningContainer}>
          <InlineAlert type="warning" message={warningMessage} />
        </View>
      ) : null}

      {times.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text.Ui size="lg" style={styles.emptyText}>
            No available timeslots
          </Text.Ui>
        </View>
      ) : (
        <ScrollView style={styles.timeslotContainer}>
          {times.map(({ time }) => (
            <Pressable
              key={time.toISOString()}
              style={styles.timeslotRow}
              onPress={() => setSelectedSlot(time.toISOString())}
            >
              {selectedSlot === time.toISOString() && <IconCheck size={18} />}
              <Text.Ui size="lg">{format(time, 'h:mm a')}</Text.Ui>
            </Pressable>
          ))}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 28,
  },
  heading: {
    marginTop: 24,
    marginBottom: 8,
  },
  warningContainer: {
    marginBottom: 8,
  },
  timeslotContainer: {
    height: 224,
  },
  timeslotRow: {
    flexDirection: 'row',
    gap: 8,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginRight: 6,
  },
  emptyContainer: {
    height: 224,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    color: 'rgba(0, 0, 0, 0.5)',
  },
});
